import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, ArrowLeft, Mail, Lock, Loader2 } from 'lucide-react';

// Schema for password login
const passwordSchema = yup.object({
  email: yup
    .string()
    .required('Email is required')
    .email('Invalid email format'),
  password: yup
    .string()
    .required('Password is required')
});

type PasswordFormData = yup.InferType<typeof passwordSchema>;

type LoginMode = 'password' | 'smart_otp';
type OtpStep = 'input' | 'verify';

const LoginPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [loginMode, setLoginMode] = useState<LoginMode>('password');
  const [otpStep, setOtpStep] = useState<OtpStep>('input');
  const [otpIdentifier, setOtpIdentifier] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpChannel, setOtpChannel] = useState<'email' | 'whatsapp'>('email');

  const { login, requestOtpSmart, verifyOtpSmart } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Password form
  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    formState: { errors: passwordErrors }
  } = useForm<PasswordFormData>({
    resolver: yupResolver(passwordSchema)
  });

  const {
    register: registerOtpEmail,
    handleSubmit: handleOtpEmailSubmit,
    formState: { errors: otpEmailErrors }
  } = useForm<{ identifier: string }>({
    resolver: yupResolver(
      yup.object({
        identifier: yup.string().required('Email or phone number is required')
      })
    )
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const message = params.get('message');
    const verified = params.get('verified');
    if (message) {
      setInfo(decodeURIComponent(message));
    } else if (verified === '1') {
      setInfo('Email verified successfully. You can now log in.');
    } else if (verified === '0') {
      setInfo('Verification link could not be validated. Please request a new one or contact support.');
    }
  }, [location.search]);

  // Reset states when switching login mode
  const handleModeSwitch = (mode: LoginMode) => {
    setLoginMode(mode);
    setError('');
    setInfo('');
    setOtpStep('input');
    setOtpCode('');
    setOtpIdentifier('');
  };

  // Password login handler
  const onPasswordSubmit = async (data: PasswordFormData) => {
    setIsLoading(true);
    setError('');
    setInfo('');

    try {
      const result = await login(data.email, data.password, rememberMe);
      
      if (!result.success) {
        setError(result.message);
        if (result.message.toLowerCase().includes('verify your email')) {
          setInfo('Please verify your email address before logging in. Check your inbox for the verification email.');
        }
        return;
      }

      navigate('/dashboard');
    } catch (err: any) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // OTP request handler
  const onOtpEmailSubmit = async (data: { identifier: string }) => {
    setIsLoading(true);
    setError('');
    setInfo('');

    try {
      const result = await requestOtpSmart(data.identifier);
      setOtpIdentifier(data.identifier);
      setOtpChannel(result.channel);
      setOtpStep('verify');
      setInfo('If an account exists, a login code has been sent.');
    } catch (err: any) {
      setError(err.message || 'Failed to send login code.');
    } finally {
      setIsLoading(false);
    }
  };

  // OTP verify handler
  const onOtpVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otpCode.length !== 6) {
      setError('Please enter a 6-digit code.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await verifyOtpSmart(otpIdentifier, otpCode);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message || 'Invalid or expired code.');
    } finally {
      setIsLoading(false);
    }
  };

  // Resend OTP handler
  const handleResendOtp = async () => {
    setIsLoading(true);
    setError('');
    setInfo('');

    try {
      await requestOtpSmart(otpIdentifier);
      setInfo(`A new login code has been sent to your ${otpChannel === 'whatsapp' ? 'WhatsApp' : 'email'}.`);
      setOtpCode('');
    } catch (err: any) {
      setError(err.message || 'Failed to resend code.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative">
      {/* Back to Home Link */}
      <div className="absolute top-6 left-6">
        <Link to="/" className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium transition-colors">
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back to Home
        </Link>
      </div>

      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <Link to="/" className="inline-block mb-8">
            <img src="/logo.png" alt="MY1QR" className="h-24 w-auto mx-auto drop-shadow-sm" />
          </Link>
          <h1 className="text-3xl font-bold text-secondary-900">Welcome back</h1>
          <p className="mt-2 text-secondary-600">
            Sign in to your MY1QR account
          </p>
        </div>

        {/* Login Mode Toggle */}
        <div className="flex rounded-lg bg-secondary-100 p-1">
          <button
            type="button"
            onClick={() => handleModeSwitch('password')}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm font-medium transition-all ${loginMode === 'password'
              ? 'bg-white text-primary-700 shadow-sm'
              : 'text-secondary-600 hover:text-secondary-900'
              }`}
          >
            <Lock className="h-4 w-4" />
            Password
          </button>
          <button
            type="button"
            onClick={() => handleModeSwitch('smart_otp')}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm font-medium transition-all ${loginMode === 'smart_otp'
              ? 'bg-white text-primary-700 shadow-sm'
              : 'text-secondary-600 hover:text-secondary-900'
              }`}
          >
            <Mail className="h-4 w-4" />
            OTP
          </button>
        </div>

        {/* Form Card */}
        <div className="card">
          {/* Info & Error Messages */}
          {info && (
            <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg mb-4">
              {info}
            </div>
          )}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
              {error}
            </div>
          )}

          {/* Password Login Form */}
          {loginMode === 'password' && (
            <form className="space-y-6" onSubmit={handlePasswordSubmit(onPasswordSubmit)}>
              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-secondary-700 mb-2">
                  Email Address
                </label>
                <input
                  {...registerPassword('email')}
                  type="email"
                  id="email"
                  className={`input-field ${passwordErrors.email ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                  placeholder="Enter your email"
                />
                {passwordErrors.email && (
                  <p className="mt-1 text-sm text-red-600">{passwordErrors.email.message}</p>
                )}
              </div>

              {/* Password Field */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-secondary-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    {...registerPassword('password')}
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    className={`input-field pr-10 ${passwordErrors.password ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-secondary-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-secondary-400" />
                    )}
                  </button>
                </div>
                {passwordErrors.password && (
                  <p className="mt-1 text-sm text-red-600">{passwordErrors.password.message}</p>
                )}
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-secondary-700">
                    Remember me
                  </label>
                </div>
                <div className="text-sm">
                  <Link to="/forgot-password" className="text-primary-600 hover:text-primary-700">
                    Forgot your password?
                  </Link>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full btn-primary text-white py-3 text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Signing In...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>
          )}

          {/* OTP Login Form - Input Step */}
          {loginMode === 'smart_otp' && otpStep === 'input' && (
            <form className="space-y-6" onSubmit={handleOtpEmailSubmit(onOtpEmailSubmit)}>
              <div>
                <label htmlFor="otp-identifier" className="block text-sm font-medium text-secondary-700 mb-2">
                  Email or Phone Number
                </label>
                <input
                  {...registerOtpEmail('identifier')}
                  type="text"
                  id="otp-identifier"
                  className={`input-field ${otpEmailErrors.identifier ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                  placeholder="Enter email or phone number"
                />
                {otpEmailErrors.identifier && (
                  <p className="mt-1 text-sm text-red-600">{otpEmailErrors.identifier.message}</p>
                )}
              </div>

              <p className="text-sm text-secondary-500">
                We'll detect and send the code via Email or WhatsApp.
              </p>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full btn-primary text-white py-3 text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Sending Code...
                  </>
                ) : (
                  <>
                    <Mail className="h-5 w-5" />
                    Send Login Code
                  </>
                )}
              </button>
            </form>
          )}

          {/* OTP Login Form - Verify Step */}
          {loginMode === 'smart_otp' && otpStep === 'verify' && (
            <form className="space-y-6" onSubmit={onOtpVerify}>
              <div className="text-center mb-4">
                <p className="text-sm text-secondary-600">
                  Enter the 6-digit code sent to your <strong className="text-secondary-800">{otpChannel === 'whatsapp' ? 'WhatsApp' : 'email'}</strong>
                </p>
                <p className="font-medium text-secondary-900">{otpIdentifier}</p>
              </div>

              <div>
                <label htmlFor="otp-code" className="block text-sm font-medium text-secondary-700 mb-2">
                  Login Code
                </label>
                <input
                  type="text"
                  id="otp-code"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="input-field text-center text-2xl tracking-widest font-mono"
                  placeholder="000000"
                  maxLength={6}
                  autoComplete="one-time-code"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading || otpCode.length !== 6}
                className="w-full btn-primary text-white py-3 text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify & Sign In'
                )}
              </button>

              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={() => {
                    setOtpStep('input');
                    setOtpCode('');
                    setError('');
                    setInfo('');
                  }}
                  className="text-secondary-600 hover:text-secondary-900"
                >
                  ← Change login
                </button>
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={isLoading}
                  className="text-primary-600 hover:text-primary-700 disabled:opacity-50"
                >
                  Resend code
                </button>
              </div>
            </form>
          )}

          {/* Signup Link */}
          <div className="text-center mt-6">
            <p className="text-secondary-600">
              Don't have an account?{' '}
              <Link to="/signup" className="text-primary-600 hover:text-primary-700 font-medium">
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
